import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Brain, RefreshCw, Copy, Sparkles, X, Plus, Skull, AlertCircle, TrendingUp, BarChart3,
  Layers, Hexagon, Triangle, Target, Zap, Save, Bell
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { api } from '../lib/api.js';
import { useEngine } from '../hooks/useEngine.js';
import { useUser } from '../stores/user.js';
import { useHistoryStore } from '../stores/history.js';
import { BACKTEST } from '../lib/formulas.js';

function pickWeighted(rng, weighted) {
  const total = weighted.reduce((s, x) => s + x.weight, 0);
  if (total <= 0) return null;
  let r = rng() * total;
  for (const w of weighted) {
    r -= w.weight;
    if (r <= 0) return w;
  }
  return weighted[weighted.length - 1];
}

function mulberry32(a) {
  return function () {
    a |= 0; a = a + 0x6D2B79F5 | 0;
    let t = a;
    t = Math.imul(t ^ t >>> 15, t | 1);
    t ^= t + Math.imul(t ^ t >>> 7, t | 61);
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}

// AI 推荐杀号:共识度优先 + BACKTEST 等级加成
// 核心逻辑:被多个独立公式共识杀的号 > 单个 high 公式杀的号
function getAIRecommendedKills(killPool) {
  const allKills = killPool.kills || [];
  const codeInfo = {};
  allKills.forEach(k => {
    if (k.code === undefined || k.code === null) return;
    if (!codeInfo[k.code]) codeInfo[k.code] = { sources: new Set() };
    if (k.name) codeInfo[k.code].sources.add(k.name);
  });

  const recommended = [];
  Object.entries(codeInfo).forEach(([codeStr, info]) => {
    let totalRate = 0, highCount = 0, midCount = 0, lowCount = 0;
    info.sources.forEach(n => {
      const bt = BACKTEST[n];
      if (bt) {
        totalRate += bt.rate;
        if (bt.level === 'high') highCount++;
        else if (bt.level === 'mid') midCount++;
        else lowCount++;
      }
    });
    const consensus = info.sources.size;
    const avgRate = consensus > 0 ? totalRate / consensus : 30;
    // 综合分(共识度优先,因为多公式共识比单公式更稳):
    //   - 共识度权重最高(每共识 +5)
    //   - high 公式加成(每个 +3)
    //   - mid 公式加成(每个 +1)
    //   - 平均命中率(基础分 × 0.5)
    const score = avgRate * 0.5 + consensus * 5 + highCount * 3 + midCount * 1;
    recommended.push({
      code: parseInt(codeStr),
      source: [...info.sources].slice(0, 3).join(' + '),
      hitRate: avgRate.toFixed(1),
      formulaCount: consensus,
      highCount,
      midCount,
      score,
    });
  });

  // 按综合分倒序,取前 5
  return recommended
    .sort((a, b) => b.score - a.score || b.formulaCount - a.formulaCount)
    .slice(0, 5);
}

// AI 推荐杀两码(对码):基于单码共识的笛卡尔积
// 简化逻辑:如果两个号都进入 AI 推荐单码(都被多个公式杀),
//         那么这两个号同时出现概率低 → 杀这个两码组合
function getAIRecommendedPairs(killPool, aiKills) {
  if (!aiKills || aiKills.length < 2) return [];
  const codes = aiKills.map(k => k.code);
  const pairs = [];
  for (let i = 0; i < codes.length; i++) {
    for (let j = i + 1; j < codes.length; j++) {
      const a = codes[i], b = codes[j];
      const key = a < b ? `${a}${b}` : `${b}${a}`;
      pairs.push({
        pair: key,
        a, b,
        confidence: aiKills[i].formulaCount + aiKills[j].formulaCount,
        reason: `${a}×${aiKills[i].formulaCount} + ${b}×${aiKills[j].formulaCount} 共识`,
      });
    }
  }
  // 按总共识度倒序,取前 8
  return pairs.sort((a, b) => b.confidence - a.confidence).slice(0, 8);
}

export default function PickPage() {
  const { result, killPool, danPool, heatMap } = useEngine();
  const userKillsFromStore = useUser(s => s.user?.userKills || []);
  const setUserKills = useUser(s => s.setUserKills);
  const isLoggedIn = useUser(s => !!s.user);

  const [type, setType] = useState('zu6');
  const [count, setCount] = useState(10);
  const [oddEven, setOddEven] = useState('mixed');
  const [bigSmall, setBigSmall] = useState('mixed');
  const [spanRange, setSpanRange] = useState([0, 9]);
  const [weights, setWeights] = useState({
    dan: 1.5, hot: 1.2, cold: 0.5, pair: 0.8,
    kill: 0.2, default: 1.0
  });
  const [picks, setPicks] = useState([]);
  const [seed, setSeed] = useState(Date.now());
  // 智能提醒: 保存选号
  const [savingSelection, setSavingSelection] = useState(false);

  // 目标期号 = 最新历史期 + 1
  const history = useHistoryStore(s => s.history);
  const targetPeriod = history[0]?.p ? String(parseInt(history[0].p) + 1) : null;

  // 杀号(单码)和杀两码(对码)
  const [localKills, setLocalKills] = useState(new Set(userKillsFromStore));
  const [localKillPairs, setLocalKillPairs] = useState(new Set());

  // 杀组选形态(组三 / 组六 / 豹子) — 新增
  const [killXingtai, setKillXingtai] = useState({ group6: false, group3: false, baozi: false });

  // 自动预测形态概率(最近 N 期历史)
  const autoXingtai = useMemo(() => {
    if (!history || history.length < 5) return null;
    // 算最近 10/20 期的形态分布 + 距上次某种形态的期数
    function classify(item) {
      const uniq = new Set([item.a, item.b, item.c]);
      if (uniq.size === 1) return 'baozi';
      if (uniq.size === 2) return 'group3';
      return 'group6';
    }
    const recent10 = history.slice(0, 10);
    const recent20 = history.slice(0, 20);
    function dist(arr) {
      const c = { group6: 0, group3: 0, baozi: 0 };
      arr.forEach(h => c[classify(h)]++);
      const t = arr.length;
      return { group6: c.group6 / t, group3: c.group3 / t, baozi: c.baozi / t, total: t };
    }
    // 距上次某种形态的期数(数字越大 = 越冷 = 越可能出)
    function gap(name) {
      for (let i = 0; i < history.length; i++) {
        if (classify(history[i]) === name) return i;
      }
      return history.length;
    }
    const d10 = dist(recent10);
    const d20 = dist(recent20);
    const gaps = { group3: gap('group3'), baozi: gap('baozi') };
    // 推荐:概率 < 18% 或 距上次 > 30 期的形态
    const recommended = [];
    if (d20.baozi < 0.05 || gaps.baozi > 30) recommended.push('baozi');
    if (d20.group3 < 0.20 || gaps.group3 > 5) recommended.push('group3');
    return {
      recent10: d10,
      recent20: d20,
      gaps,
      recommended,
      currentType: classify(history[0]),  // 最新一期
    };
  }, [history]);

  // 杀组选号码(0-9,杀掉包含此数字的所有组选号码)
  // 跟"杀单码"是同一个机制,但归类到"杀组选"语义下
  const [killXingtaiByCode, setKillXingtaiByCode] = useState(new Set());
  const [adoptFlash, setAdoptFlash] = useState(false);

  // 定位杀(基于历史 2 期数据,公式计算必杀号)
  const [posKill, setPosKill] = useState(null);
  const [posKillLoading, setPosKillLoading] = useState(false);
  const loadPositionalKill = async () => {
    setPosKillLoading(true);
    try {
      const r = await api.positionalKill();
      setPosKill(r);
    } catch (e) {
      toast.error('定位杀加载失败:' + e.message);
    } finally {
      setPosKillLoading(false);
    }
  };
  useEffect(() => {
    loadPositionalKill();
  }, []);

  // 同步到 user store(让大底池也生效)
  useEffect(() => {
    setUserKills(Array.from(localKills));
  }, [localKills]);

  // AI 推荐
  const aiKills = useMemo(() => getAIRecommendedKills(killPool), [killPool]);
  const aiPairs = useMemo(() => getAIRecommendedPairs(killPool, aiKills), [killPool, aiKills]);

  // toggle 单码杀号
  const toggleKill = (code) => {
    setLocalKills(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  // toggle 杀两码
  const toggleKillPair = (a, b) => {
    const key = a < b ? `${a}${b}` : `${b}${a}`;
    setLocalKillPairs(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // 一键采纳 AI 推荐
  const adoptAIRecommended = () => {
    const next = new Set(localKills);
    aiKills.forEach(k => next.add(k.code));
    setLocalKills(next);
    setLocalKillPairs(prev => {
      const next = new Set(prev);
      aiPairs.forEach(p => next.add(p.pair));
      return next;
    });
    toast.success(`已采纳 ${aiKills.length} 个杀号 + ${aiPairs.length} 个杀两码`);
  };

  // 清空所有杀号
  const clearKills = () => {
    setLocalKills(new Set());
    setLocalKillPairs(new Set());
    toast.success('已清空');
  };

  // 把已杀号同步给 useEngine(让大底池用)
  useEffect(() => {
    // 通过 useUser 同步
  }, [localKills]);

  const run = () => {
    const rng = mulberry32(seed);
    const axisNums = new Set((killPool.axis && killPool.axis.axisNumbers) || []);
    const shiqiweiKill = new Set(
      (killPool.kills || [])
        .filter(k => k.name === '上期十位直接杀')
        .map(k => k.code)
    );
    const allKills = killPool.kills || [];

    const highSelectSet = new Set(
      Object.entries(BACKTEST)
        .filter(([n, bt]) => bt.level === 'high' && bt.base === 30)
        .map(([n]) => allKills.find(k => k.name === n))
        .filter(Boolean)
        .map(k => k.code)
    );

    const danBai = danPool.bai.map(d => d.code);
    const danShi = danPool.shi.map(d => d.code);
    const danGe = danPool.ge.map(d => d.code);
    const allDans = [...new Set([...danBai, ...danShi, ...danGe, danPool.mainDan])];
    const danSet = new Set(allDans);
    const hotSet = new Set([...(heatMap?.hot30 || []), ...(heatMap?.hot4 || [])]);
    const coldSet = new Set(heatMap?.cold || []);
    const pairSet = new Set(
      [...(killPool?.baiAll || []), ...(killPool?.shiAll || []), ...(killPool?.geAll || [])].map(x => x.code)
    );

    const buildWeight = () => {
      const w = [];
      for (let i = 0; i < 10; i++) {
        let weight = weights.default;
        // 用户手动杀号 / AI 推荐杀号 → 权重 = 0(完全排除)
        if (localKills.has(i)) {
          w.push({ code: i, weight: 0, isKill: true, isManual: true });
          continue;
        }
        const isKill = axisNums.has(i) || shiqiweiKill.has(i);
        if (isKill) weight = weights.kill;
        if (highSelectSet.has(i)) weight = Math.max(weight, 1.5);
        if (danSet.has(i)) weight = Math.max(weight, weights.dan);
        if (hotSet.has(i)) weight = Math.max(weight, weights.hot);
        if (coldSet.has(i)) weight = Math.max(weight, weights.cold);
        if (pairSet.has(i)) weight = Math.max(weight, weights.pair);
        w.push({ code: i, weight, isKill, isHigh: highSelectSet.has(i) });
      }
      return w;
    };
    const weighted = buildWeight();

    const result = [];
    const seen = new Set();
    const maxAttempts = count * 500;
    let attempts = 0;

    // 检查杀两码:triple 中是否包含 localKillPairs 里的对
    const hasKillPair = (triple) => {
      for (let i = 0; i < triple.length; i++) {
        for (let j = i + 1; j < triple.length; j++) {
          const a = triple[i], b = triple[j];
          const key = a < b ? `${a}${b}` : `${b}${a}`;
          if (localKillPairs.has(key)) return true;
        }
      }
      return false;
    };

    while (result.length < count && attempts < maxAttempts) {
      attempts++;
      const a = pickWeighted(rng, weighted);
      const b = pickWeighted(rng, weighted);
      const c = pickWeighted(rng, weighted);
      if (!a || !b || !c) break;
      const triple = [a.code, b.code, c.code];
      const uniq = new Set(triple);
      if (type === 'zu6' && uniq.size !== 3) continue;
      if (type === 'zu3' && uniq.size !== 2) continue;
      if (type === 'single' && uniq.size === 1) continue;
      // 杀组选号码(0-9):只要 triple 包含被杀数字 → 整注排除
      if (killXingtaiByCode.size > 0 && triple.some(c => killXingtaiByCode.has(c))) continue;
      // 杀组选形态(用户手动)
      if (killXingtai.group6 && uniq.size === 3) continue;   // 杀组六
      if (killXingtai.group3 && uniq.size === 2) continue;   // 杀组三
      if (killXingtai.baozi && uniq.size === 1) continue;   // 杀豹子
      const oddCount = triple.filter(x => x % 2 === 1).length;
      if (oddEven === '2odd1even' && oddCount !== 2) continue;
      if (oddEven === '2even1odd' && oddCount !== 1) continue;
      if (oddEven === 'allodd' && oddCount !== 3) continue;
      if (oddEven === 'alleven' && oddCount !== 0) continue;
      const bigCount = triple.filter(x => x >= 5).length;
      if (bigSmall === '2big1small' && bigCount !== 2) continue;
      if (bigSmall === '2small1big' && bigCount !== 1) continue;
      if (bigSmall === 'allbig' && bigCount !== 3) continue;
      if (bigSmall === 'allsmall' && bigCount !== 0) continue;
      const span = Math.max(...triple) - Math.min(...triple);
      if (span < spanRange[0] || span > spanRange[1]) continue;
      if (hasKillPair(triple)) continue;  // 杀两码过滤
      const key = triple.join('');
      if (seen.has(key)) continue;
      seen.add(key);
      const tags = [];
      if (a.isHigh) tags.push('⭐HIGH');
      else if (a.isKill) tags.push('⚠杀号');
      if (danSet.has(a.code)) tags.push('💎胆');
      if (hotSet.has(a.code)) tags.push('🔥热');
      if (coldSet.has(a.code)) tags.push('❄冷');
      result.push({ code: key, a, b, c, tags: tags.join('·'), xingtai: uniq.size === 3 ? '组六' : uniq.size === 2 ? '组三' : '豹子', span, sum: a.code + b.code + c.code });
    }
    setPicks(result);
    toast.success(`已生成 ${result.length} 注`);
  };

  // 智能提醒:保存当前 picks
  const handleSaveSelection = async () => {
    if (picks.length === 0) {
      toast.error('请先生成选号');
      return;
    }
    if (!isLoggedIn) {
      toast.error('请先登录');
      return;
    }
    if (!targetPeriod) {
      toast.error('期号未就绪,请稍后重试');
      return;
    }
    setSavingSelection(true);
    try {
      const r = await api.selections.add({
        period: targetPeriod,
        picks: picks.map(p => p.code),
        pool_level: 'manual',
        source: 'pick_page',
        note: `${picks.length} 注 ${picks[0]?.xingtai || ''}`,
      });
      if (r?.ok) {
        if (r.status === 'hit') {
          toast.success(`🎉 恭喜!该期已开奖,您保存的选号命中 ${r.hit_pick}`, { duration: 5000 });
        } else if (r.status === 'missed') {
          toast('该期已开奖,未中。下一期继续 💪', { icon: '🎯', duration: 3000 });
        } else {
          toast.success(`已保存!开奖后会自动通知 (期号 ${r.period})`, { duration: 4000 });
        }
      } else {
        toast.error(r?.error || '保存失败');
      }
    } catch (e) {
      toast.error('保存失败: ' + e.message);
    } finally {
      setSavingSelection(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="card">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Brain size={22} className="text-brand-rose" />
          智能选号 v2
        </h1>
        <p className="text-sm text-ink-3 mt-1">多策略权重可调 + 实时回测角标 + 复式自动算</p>
      </div>

      {/* 杀号区(AI 推荐 + 手动) */}
      <div className="card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-sm font-semibold flex items-center gap-2">
              <Skull size={14} className="text-accent-danger" />
              杀号 / 杀两码
            </div>
            <p className="text-[10px] text-ink-3 mt-0.5">点选号码 / 两码组合,智选时自动排除</p>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={adoptAIRecommended}
              className="btn-ghost text-[10px] flex items-center gap-1 py-1 px-2"
            >
              <Sparkles size={10} /> 一键采纳 AI 推荐
            </button>
            <button
              onClick={clearKills}
              className="btn-ghost text-[10px] flex items-center gap-1 py-1 px-2"
            >
              <X size={10} /> 清空
            </button>
          </div>
        </div>

        {/* AI 推荐杀号 */}
        {aiKills.length > 0 && (
          <div className="mb-3 p-2 rounded-lg bg-brand-purple/5 border border-brand-purple/20">
            <div className="text-[10px] text-brand-purple font-semibold mb-1.5 flex items-center gap-1">
              <TrendingUp size={10} /> AI 推荐杀号(按综合分排序,前 5)
            </div>
            <div className="flex flex-wrap gap-1.5">
              {aiKills.map(k => {
                const isKilled = localKills.has(k.code);
                const isHigh = k.highCount > 0;
                return (
                  <button
                    key={k.source + k.code}
                    onClick={() => toggleKill(k.code)}
                    title={`${k.formulaCount} 个公式:${k.source}\n高命中率: ${k.highCount}个, 中: ${k.midCount}个`}
                    className={clsx(
                      'px-2 py-1 rounded text-[10px] flex items-center gap-1 border transition',
                      isKilled
                        ? 'bg-accent-danger/20 border-accent-danger text-accent-danger line-through'
                        : isHigh
                          ? 'bg-brand-gold/10 border-brand-gold/40 text-brand-gold hover:border-accent-danger/40'
                          : 'bg-white/5 border-white/10 text-ink-2 hover:border-accent-danger/40'
                    )}
                  >
                    <span className="font-mono font-bold text-sm">{k.code}</span>
                    <span className={clsx(
                      'px-1 rounded text-[9px]',
                      isHigh ? 'bg-brand-gold/20 text-brand-gold' : 'bg-brand-purple/20 text-brand-purple'
                    )}>
                      ×{k.formulaCount}
                    </span>
                    <span className="text-ink-3">{k.hitRate}%</span>
                    {isKilled && <Skull size={8} />}
                  </button>
                );
              })}
            </div>
            <div className="text-[9px] text-ink-3 mt-1.5">
              💡 金色=有高命中率公式,紫色=中/低公式,数字=共识度,百分比=平均命中率
            </div>
          </div>
        )}

        {/* 手动杀号 0-9 */}
        <div className="mb-3">
          <div className="text-[10px] text-ink-3 mb-1.5 flex items-center gap-1">
            <AlertCircle size={10} /> 手动杀号(0-9,点击切换)
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {Array.from({ length: 10 }, (_, i) => {
              const isKilled = localKills.has(i);
              return (
                <button
                  key={i}
                  onClick={() => toggleKill(i)}
                  className={clsx(
                    'w-12 h-12 rounded-xl text-lg font-mono font-bold transition border-2',
                    isKilled
                      ? 'bg-accent-danger/30 border-accent-danger text-accent-danger line-through'
                      : 'bg-white/5 border-white/10 text-ink-1 hover:border-accent-danger/40'
                  )}
                >
                  {i}
                </button>
              );
            })}
          </div>
        </div>

        {/* 杀两码(对码) */}
        <div>
          <div className="text-[10px] text-ink-3 mb-1.5 flex items-center gap-1">
            <AlertCircle size={10} /> 杀两码组合(智选时不选含此两码的注)
          </div>
          <div className="grid grid-cols-9 sm:grid-cols-10 gap-1">
            {(() => {
              const allPairs = [];
              for (let i = 0; i < 10; i++) {
                for (let j = i + 1; j < 10; j++) {
                  allPairs.push([i, j]);
                }
              }
              return allPairs.map(([a, b]) => {
                const key = `${a}${b}`;
                const isKilled = localKillPairs.has(key);
                const isAIRec = aiPairs.some(p => p.pair === key);
                return (
                  <button
                    key={key}
                    onClick={() => toggleKillPair(a, b)}
                    className={clsx(
                      'h-7 rounded text-[10px] font-mono font-bold transition border',
                      isKilled
                        ? 'bg-accent-danger/20 border-accent-danger text-accent-danger line-through'
                        : isAIRec
                          ? 'bg-brand-purple/10 border-brand-purple/40 text-brand-purple hover:border-accent-danger/40'
                          : 'bg-white/5 border-white/10 text-ink-3 hover:border-accent-danger/40'
                    )}
                    title={isAIRec
                      ? `AI 推荐:${aiPairs.find(p => p.pair === key)?.reason || '多公式共识'}`
                      : `${a}${b} 组合(手动)`
                    }
                  >
                    {a}{b}
                  </button>
                );
              });
            })()}
          </div>
        </div>

        {/* 统计 + 风险提示 */}
        <div className="mt-2">
          <div className="text-[10px] text-ink-3 flex items-center gap-3 flex-wrap">
            <span>已杀单码: <span className="text-accent-danger font-mono">{localKills.size}</span> / 10</span>
            <span>已杀两码: <span className="text-accent-danger font-mono">{localKillPairs.size}</span> / 45</span>
            <span>剩余候选: <span className="text-brand-cyan font-mono">{(() => {
              // 准确计算:三位独立概率
              // 1 个单码砍 271 注(1000 - 729 = 271)
              // 多个单码: 1000 × ((10-k)/10)³ 的近似
              // 减去杀两码:每个两码砍 10 注(固定两位)
              if (localKills.size === 0 && localKillPairs.size === 0) return 1000;
              const kills = localKills.size;
              const pairs = localKillPairs.size;
              const k = Math.min(10, kills);
              // 粗略:每个单码独立,候选剩 (10-k)³
              let estimate = Math.pow(10 - k, 3);
              // 杀两码会进一步减(每个 ~10 注,但跟单码重叠)
              estimate = Math.max(0, estimate - pairs * 10);
              return estimate;
            })()}</span> 注</span>
          </div>
          {localKills.size >= 4 && (
            <div className="mt-1.5 text-[10px] text-accent-warn flex items-start gap-1">
              <AlertCircle size={10} className="flex-shrink-0 mt-0.5" />
              <span>
                ⚠ 杀 {localKills.size} 个单码风险较高:回测每多杀 1 个,真值存活率下降 ~10%。
                建议只在 AI 共识≥3 时杀(单期 1-2 个号)。
              </span>
            </div>
          )}
          {localKillPairs.size >= 3 && (
            <div className="mt-1 text-[10px] text-accent-warn flex items-start gap-1">
              <AlertCircle size={10} className="flex-shrink-0 mt-0.5" />
              <span>
                ⚠ 杀 {localKillPairs.size} 个两码,候选会进一步缩小;如果选不到 10 注,会被截断。
              </span>
            </div>
          )}
        </div>
      </div>

      {/* 杀组选卡片 — MAX v4 新增 */}
      <div className={clsx('card transition-all', adoptFlash ? 'ring-2 ring-brand-cyan shadow-lg' : '')}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-sm font-semibold flex items-center gap-2">
              <Layers size={14} className="text-brand-cyan" />
              杀组选形态
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-brand-cyan/15 text-brand-cyan font-normal">MAX v4</span>
              {(killXingtai.group6 || killXingtai.group3 || killXingtai.baozi) && (
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-accent-danger/15 text-accent-danger font-bold">
                  已启用 {Object.entries(killXingtai).filter(([_,v]) => v).map(([k]) => k).join('+')}
                </span>
              )}
            </div>
            <p className="text-[10px] text-ink-3 mt-0.5">排除特定形态的注(组六/组三/豹子)— 比杀单码粒度更粗</p>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => {
                // MAX v7: 优先用公式的 killXingtai,兜底用 autoXingtai(recent20 概率 < 18% 自动杀)
                const formulaKills = result.killXingtai || { group6: [], group3: [], baozi: [] };
                const hasFormula = formulaKills.group6.length || formulaKills.group3.length || formulaKills.baozi.length;
                const p20 = autoXingtai?.recent20 || {};
                const next = { group6: false, group3: false, baozi: false };
                const reasons = [];
                // 1. 公式规则(7 条形态规则)
                if (formulaKills.group6.length >= 1) { next.group6 = true; reasons.push(`公式杀组六(${formulaKills.group6.length}条)`); }
                if (formulaKills.group3.length >= 1) { next.group3 = true; reasons.push(`公式杀组三(${formulaKills.group3.length}条)`); }
                if (formulaKills.baozi.length >= 1)  { next.baozi  = true; reasons.push(`公式杀豹子(${formulaKills.baozi.length}条)`); }
                // 2. 兜底:概率 < 18% 杀 + 冷期过长杀
                if (!hasFormula) {
                  if ((p20.baozi  || 0) < 0.18) { next.baozi  = true; reasons.push(`概率${((p20.baozi||0)*100).toFixed(0)}% < 18%`); }
                  if ((p20.group3 || 0) < 0.18) { next.group3 = true; reasons.push(`概率${((p20.group3||0)*100).toFixed(0)}% < 18%`); }
                  if ((p20.group6 || 0) < 0.18) { next.group6 = true; reasons.push(`概率${((p20.group6||0)*100).toFixed(0)}% < 18%`); }
                }
                setKillXingtai(next);
                const enabled = Object.entries(next).filter(([_, v]) => v).map(([k]) => k).join('+');
                if (enabled) {
                  toast.success(`已采纳: ${enabled} · ${reasons.slice(0, 2).join(' + ')}`);
                  setAdoptFlash(true);
                  setTimeout(() => setAdoptFlash(false), 2000);
                } else {
                  toast('AI 暂未给出杀组选建议(概率都正常)', { icon: 'ℹ️' });
                  setAdoptFlash(true);
                  setTimeout(() => setAdoptFlash(false), 1500);
                }
              }}
              className="btn-ghost text-[10px] flex items-center gap-1 py-1 px-2"
            >
              <Sparkles size={10} /> 一键采纳 AI
            </button>
            <button
              onClick={() => {
                setKillXingtai({ group6: false, group3: false, baozi: false });
                toast.success('已清空杀组选');
              }}
              className="btn-ghost text-[10px] flex items-center gap-1 py-1 px-2"
            >
              <X size={10} /> 清空
            </button>
          </div>
        </div>

        {/* AI 杀组选规则(从 formulas.computeKillXingtai) */}
        {result.killXingtai && (result.killXingtai.group6.length > 0 || result.killXingtai.group3.length > 0 || result.killXingtai.baozi.length > 0) && (
          <div className="mb-3 p-2 rounded-lg bg-brand-cyan/5 border border-brand-cyan/20">
            <div className="text-[10px] text-brand-cyan font-semibold mb-1.5 flex items-center gap-1">
              <TrendingUp size={10} /> AI 杀组选规则(200 期回测)
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {[
                { key: 'group6', label: '组六', Icon: Hexagon, bg: 'bg-brand-purple/5', border: 'border-brand-purple/20', text: 'text-brand-purple', chipBg: 'bg-brand-purple/20', chipText: 'text-brand-purple' },
                { key: 'group3', label: '组三', Icon: Triangle, bg: 'bg-brand-rose/5', border: 'border-brand-rose/20', text: 'text-brand-rose', chipBg: 'bg-brand-rose/20', chipText: 'text-brand-rose' },
                { key: 'baozi', label: '豹子', Icon: Layers, bg: 'bg-brand-gold/5', border: 'border-brand-gold/20', text: 'text-brand-gold', chipBg: 'bg-brand-gold/20', chipText: 'text-brand-gold' },
              ].map(({ key, label, Icon, bg, border, text, chipBg, chipText }) => {
                const rules = result.killXingtai[key] || [];
                return (
                  <div key={key} className={clsx(
                    'p-2 rounded border transition',
                    killXingtai[key]
                      ? 'bg-accent-danger/15 border-accent-danger/60'
                      : clsx(bg, border)
                  )}>
                    <div className="flex items-center gap-1.5 mb-1">
                      <Icon size={12} className={text} />
                      <span className="text-[10px] font-semibold">杀{label}</span>
                      <span className={clsx(
                        'ml-auto text-[9px] px-1 rounded',
                        killXingtai[key] ? 'bg-accent-danger/30 text-accent-danger' : clsx(chipBg, chipText)
                      )}>
                        {killXingtai[key] ? '已启用' : `${rules.length} 条规则`}
                      </span>
                    </div>
                    {rules.length === 0 ? (
                      <div className="text-[9px] text-ink-3">不杀</div>
                    ) : (
                      <div className="space-y-0.5">
                        {rules.map((r, i) => (
                          <div key={i} className="text-[9px] text-ink-2 flex items-center gap-1">
                            <span className="font-mono text-brand-cyan">{r.rate.toFixed(1)}%</span>
                            <span className="truncate">{r.name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="text-[9px] text-ink-3 mt-1.5">
              💡 数字是回测命中率(200 期),点击下方按钮启用
            </div>
          </div>
        )}

        {/* 自动预测形态概率 — 新增 v4.1 */}
        {autoXingtai && (
          <div className="mb-3 p-2 rounded-lg bg-gradient-to-br from-brand-purple/10 to-brand-cyan/5 border border-brand-purple/30">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] text-brand-purple font-semibold flex items-center gap-1">
                <BarChart3 size={10} /> 形态概率预测(自动)
              </div>
              <div className="text-[9px] text-ink-3">最近 {autoXingtai.recent20.total} 期</div>
            </div>
            <div className="space-y-1.5">
              {[
                { key: 'group6', label: '组六', color: 'brand-purple', prob: autoXingtai.recent20.group6 },
                { key: 'group3', label: '组三', color: 'brand-rose',   prob: autoXingtai.recent20.group3 },
                { key: 'baozi',  label: '豹子', color: 'brand-gold',   prob: autoXingtai.recent20.baozi },
              ].map(({ key, label, color, prob }) => {
                const pct = (prob * 100).toFixed(1);
                const gap = autoXingtai.gaps[key];
                const recommended = autoXingtai.recommended.includes(key);
                return (
                  <div key={key} className={clsx(
                    'p-1.5 rounded border flex items-center gap-2',
                    recommended ? 'bg-accent-danger/10 border-accent-danger/40' : `bg-${color}/5 border-${color}/20`
                  )}>
                    <div className="text-[10px] text-ink-1 w-8">{label}</div>
                    <div className="flex-1 h-1.5 bg-bg-3 rounded-full overflow-hidden">
                      <div className={clsx('h-full bg-', color)} style={{ width: `${Math.min(100, prob * 100)}%` }} />
                    </div>
                    <div className={clsx('text-[10px] font-mono w-12 text-right', recommended ? 'text-accent-danger font-bold' : `text-${color}`)}>
                      {pct}%
                    </div>
                    {gap !== undefined && gap > 5 && (
                      <div className="text-[9px] text-amber-400" title={`距上次${label} ${gap} 期`}>
                        冷{gap}
                      </div>
                    )}
                    {recommended && (
                      <div className="text-[9px] px-1 rounded bg-accent-danger/30 text-accent-danger font-bold">杀</div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-2 text-[9px] text-ink-3 leading-relaxed">
              💡 概率基于最近 {autoXingtai.recent20.total} 期形态分布 + 距上次冷热; 冷 = 距上次出现 N 期(越大越冷越可能出); 杀 = 系统推荐(概率 &lt; 18% 或冷期过长)
            </div>
          </div>
        )}

        {/* 3 个 toggle 按钮 */}
        <div className="grid grid-cols-3 gap-2">
          {[
            { key: 'group6', label: '杀组六', desc: '不选 3 个不同号', Icon: Hexagon, idleBg: 'bg-brand-purple/5', idleBorder: 'border-brand-purple/40', idleText: 'text-brand-purple', chipBg: 'bg-brand-purple/20', chipText: 'text-brand-purple' },
            { key: 'group3', label: '杀组三', desc: '不选 2 同 1 不同', Icon: Triangle, idleBg: 'bg-brand-rose/5', idleBorder: 'border-brand-rose/40', idleText: 'text-brand-rose', chipBg: 'bg-brand-rose/20', chipText: 'text-brand-rose' },
            { key: 'baozi', label: '杀豹子', desc: '不选 3 同号', Icon: Layers, idleBg: 'bg-brand-gold/5', idleBorder: 'border-brand-gold/40', idleText: 'text-brand-gold', chipBg: 'bg-brand-gold/20', chipText: 'text-brand-gold' },
          ].map(({ key, label, desc, Icon, idleBg, idleBorder, idleText, chipBg, chipText }) => {
            const enabled = killXingtai[key];
            const rules = result.killXingtai?.[key] || [];
            const hasAIRec = rules.length > 0;
            return (
              <button
                key={key}
                onClick={() => setKillXingtai(s => ({ ...s, [key]: !s[key] }))}
                className={clsx(
                  'p-3 rounded-lg border text-left transition',
                  enabled
                    ? 'bg-accent-danger/15 border-accent-danger/60 text-accent-danger'
                    : hasAIRec
                      ? clsx(idleBg, idleBorder, 'text-ink-1', 'hover:border-accent-danger/40')
                      : 'bg-white/5 border-white/10 text-ink-3 hover:border-accent-danger/40'
                )}
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <Icon size={14} className={enabled ? 'text-accent-danger' : idleText} />
                  <span className="text-sm font-semibold">{label}</span>
                  {hasAIRec && !enabled && (
                    <span className={clsx('text-[9px] px-1 rounded', chipBg, chipText)}>AI</span>
                  )}
                </div>
                <div className="text-[9px] text-ink-3">{desc}</div>
                {hasAIRec && (
                  <div className="text-[9px] text-ink-2 mt-1">
                    最高 {Math.max(...rules.map(r => r.rate)).toFixed(0)}%
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* 杀组选 + 形态选择提示 */}
        {(killXingtai.group6 || killXingtai.group3 || killXingtai.baozi) && (
          <div className="mt-2 text-[10px] text-accent-warn flex items-start gap-1">
            <AlertCircle size={10} className="flex-shrink-0 mt-0.5" />
            <span>
              启用杀组选:形态选项失效(因为杀形态已限制范围)。如想全形态,先清空。
            </span>
          </div>
        )}

        {/* 定位杀(基于历史数据公式计算必杀号) */}
        <div className="mt-3">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-sm font-semibold flex items-center gap-2">
                <Target size={14} className="text-accent-danger" />
                定位杀 <span className="text-[10px] text-ink-3">(5 折交叉验证 · 公开 3D 杀号公式)</span>
                {posKill && posKill.backtest && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-accent-warn/20 text-accent-warn font-mono" title={`5 折 CV平均杀对率 ${(posKill.backtest.cv_avg.overall*100).toFixed(0)}%`}>
                    5 折 CV 平均 {Math.round(posKill.backtest.cv_avg.overall * 100)}% 杀对率
                  </span>
                )}
                {posKill && posKill.backtest && posKill.backtest.auto_update && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-400 font-mono" title={posKill.backtest.note}>
                    🤖 每周自动更新
                  </span>
                )}
                {posKill && posKill.backtest && posKill.backtest.auto_update === false && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400 font-mono" title="当前用 hardcoded 公式,cron 还没跑过">
                    ⚠ fallback
                  </span>
                )}
              </div>
              <p className="text-[10px] text-ink-3 mt-0.5">
                选 <span className="text-accent-danger font-bold">个位</span> 最稳 (5 折 100%)。点击下方数字自动加入"杀组选号码"集合
              </p>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={loadPositionalKill}
                disabled={posKillLoading}
                className="btn-ghost text-[10px] flex items-center gap-1 py-1 px-2"
              >
                <RefreshCw size={10} className={posKillLoading ? 'animate-spin' : ''} />
                刷新
              </button>
            </div>
          </div>

          {posKill && posKill.ok && (
            <div className="grid grid-cols-3 gap-2">
              {/* 百位 - CV 92.5% / 全 56 期 100% */}
              <div className="bg-white/3 border border-white/10 rounded-lg p-2">
                <div className="text-[10px] text-ink-3 mb-1 flex items-center justify-between">
                  <span>百位</span>
                  <span className="text-ink-1 font-mono">93%</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {posKill.hundred.kills.map(n => (
                    <button
                      key={`ph-${n}`}
                      onClick={() => {
                        setKillXingtaiByCode(prev => {
                          const next = new Set(prev);
                          if (next.has(n)) next.delete(n);
                          else next.add(n);
                          return next;
                        });
                      }}
                      className={clsx(
                        'w-7 h-7 rounded text-xs font-mono font-bold transition border',
                        killXingtaiByCode.has(n)
                          ? 'bg-accent-danger/25 border-accent-danger text-accent-danger line-through'
                          : 'bg-white/5 border-white/10 text-ink-1 hover:border-accent-danger hover:text-accent-danger'
                      )}
                      title={`百位 5 折 CV 平均 ${(posKill.hundred.rate*100).toFixed(0)}% (${posKill.hundred.formula})`}
                    >{n}</button>
                  ))}
                </div>
              </div>

              {/* 十位 - CV 90% / 全 56 期 100% */}
              <div className="bg-white/3 border border-white/10 rounded-lg p-2">
                <div className="text-[10px] text-ink-3 mb-1 flex items-center justify-between">
                  <span>十位</span>
                  <span className="text-ink-1 font-mono">90%</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {posKill.ten.kills.map(n => (
                    <button
                      key={`pt-${n}`}
                      onClick={() => {
                        setKillXingtaiByCode(prev => {
                          const next = new Set(prev);
                          if (next.has(n)) next.delete(n);
                          else next.add(n);
                          return next;
                        });
                      }}
                      className={clsx(
                        'w-7 h-7 rounded text-xs font-mono font-bold transition border',
                        killXingtaiByCode.has(n)
                          ? 'bg-accent-danger/25 border-accent-danger text-accent-danger line-through'
                          : 'bg-white/5 border-white/10 text-ink-1 hover:border-accent-danger hover:text-accent-danger'
                      )}
                      title={`十位 5 折 CV 平均 ${(posKill.ten.rate*100).toFixed(0)}% (${posKill.ten.formula})`}
                    >{n}</button>
                  ))}
                </div>
              </div>

              {/* 个位 - CV 100% / 全 56 期 100% ⭐最稳 */}
              <div className="bg-white/3 border border-accent-danger/40 rounded-lg p-2 relative">
                <span className="absolute -top-1.5 -right-1.5 text-[8px] bg-accent-danger text-white px-1.5 py-0.5 rounded-full font-bold shadow-lg">最稳</span>
                <div className="text-[10px] text-ink-3 mb-1 flex items-center justify-between">
                  <span className="text-accent-danger font-bold">个位 ⭐</span>
                  <span className="text-accent-danger font-mono font-bold">100%</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {posKill.unit.kills.map(n => (
                    <button
                      key={`pu-${n}`}
                      onClick={() => {
                        setKillXingtaiByCode(prev => {
                          const next = new Set(prev);
                          if (next.has(n)) next.delete(n);
                          else next.add(n);
                          return next;
                        });
                      }}
                      className={clsx(
                        'w-7 h-7 rounded text-xs font-mono font-bold transition border',
                        killXingtaiByCode.has(n)
                          ? 'bg-accent-danger/30 border-accent-danger text-accent-danger line-through'
                          : 'bg-accent-danger/15 border-accent-danger/50 text-accent-danger hover:bg-accent-danger/30'
                      )}
                      title={`个位 5 折 CV 100% (${posKill.unit.formula})`}
                    >{n}</button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {posKill && posKill.ok && posKill.recommend && (
            <div className="mt-2 space-y-1.5">
              {/* 回测元数据(真实数据 + 5 折 CV) */}
              {posKill.backtest && (
                <div className="text-[9px] text-ink-3 bg-black/20 rounded px-2 py-1.5 leading-relaxed">
                  <div className="flex items-center gap-1 mb-0.5">
                    <BarChart3 size={9} className="text-accent-warn" />
                    <span className="text-accent-warn font-semibold">5 折交叉验证 (前向时序)</span>
                  </div>
                  <div>全数据: <span className="text-ink-1 font-mono">{posKill.backtest.total_periods} 期</span> ({posKill.backtest.full_range})</div>
                  <div>方法: <span className="text-ink-2">{posKill.backtest.method}</span></div>
                  <div className="mt-1 grid grid-cols-3 gap-2">
                    <div>
                      <div className="text-ink-2">百位</div>
                      <div className="text-accent-warn font-mono">CV 93%</div>
                      <div className="text-ink-3 text-[8px]">{posKill.backtest.cv_fold_rates.hundred.map(r => Math.round(r*100)).join('/')}%</div>
                    </div>
                    <div>
                      <div className="text-ink-2">十位</div>
                      <div className="text-accent-warn font-mono">CV 90%</div>
                      <div className="text-ink-3 text-[8px]">{posKill.backtest.cv_fold_rates.ten.map(r => Math.round(r*100)).join('/')}%</div>
                    </div>
                    <div>
                      <div className="text-ink-2">个位</div>
                      <div className="text-accent-danger font-mono">CV 100%</div>
                      <div className="text-ink-3 text-[8px]">{posKill.backtest.cv_fold_rates.unit.map(r => Math.round(r*100)).join('/')}%</div>
                    </div>
                  </div>
                  <div className="text-accent-warn font-semibold mt-1">{posKill.backtest.note}</div>
                </div>
              )}

              {/* 取交集(与现有杀号重叠的最准) */}
              {(() => {
                const currentKills = new Set();
                if (killPool && killPool.kills) {
                  killPool.kills.forEach(k => {
                    if (k.name && k.name.includes('百位') && k.code !== undefined) currentKills.add(k.code);
                  });
                }
                const aiKillCodes = new Set((aiKills || []).map(k => k.code));
                const allPosKills = [...new Set([
                  ...posKill.hundred.kills,
                  ...posKill.ten.kills,
                  ...posKill.unit.kills
                ])];
                const intersection = allPosKills.filter(n => aiKillCodes.has(n));
                const hasOverlap = intersection.length > 0;
                return (
                  <div className="text-[10px] text-ink-2 flex items-center gap-1.5 bg-gradient-to-r from-yellow-500/10 to-accent-danger/10 border border-yellow-500/30 rounded px-2 py-1.5">
                    <Zap size={11} className="text-yellow-400 flex-shrink-0" />
                    <span className="font-semibold">
                      双重杀号(与现有 AI 重叠):
                    </span>
                    <span className="text-yellow-300 font-mono font-bold">
                      {hasOverlap ? intersection.join(', ') : '本次无重叠'}
                    </span>
                    {hasOverlap && (
                      <button
                        onClick={() => {
                          const next = new Set(killXingtaiByCode);
                          intersection.forEach(n => next.add(n));
                          setKillXingtaiByCode(next);
                          toast.success(`已采纳 ${intersection.length} 个双重杀号`);
                        }}
                        className="ml-auto text-[9px] px-2 py-0.5 rounded bg-yellow-500/30 text-yellow-300 hover:bg-yellow-500/50 font-bold"
                      >采纳</button>
                    )}
                  </div>
                );
              })()}

              <div className="text-[10px] text-ink-3 flex items-center gap-1.5 bg-white/3 rounded px-2 py-1.5">
                <Sparkles size={10} className="text-brand-purple flex-shrink-0" />
                <span>定位杀推荐:<span className="text-accent-danger font-mono font-bold mx-1">
                  {posKill.recommend.must_kill.join(', ')}
                </span>(个位,CV 100% 杀对)</span>
                <button
                  onClick={() => {
                    const next = new Set(killXingtaiByCode);
                    posKill.recommend.must_kill.forEach(n => next.add(n));
                    setKillXingtaiByCode(next);
                    toast.success(`已采纳 ${posKill.recommend.must_kill.length} 个推荐必杀号`);
                  }}
                  className="ml-auto text-[9px] px-1.5 py-0.5 rounded bg-accent-danger/20 text-accent-danger hover:bg-accent-danger/30"
                >一键采纳</button>
              </div>
            </div>
          )}

          {posKill && !posKill.ok && (
            <div className="text-[10px] text-accent-warn">
              ⚠ 定位杀暂不可用:{posKill.error || '数据不足'}
            </div>
          )}
        </div>

        {/* 分隔线 */}
        <div className="my-4 border-t border-white/10" />

        {/* 杀组选号码(0-9):杀包含此数字的全部组选号码 */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="text-sm font-semibold flex items-center gap-2">
                <Skull size={14} className="text-accent-danger" />
                杀组选号码(0-9)
              </div>
              <p className="text-[10px] text-ink-3 mt-0.5">
                点选数字 → 排除所有<strong>包含此数字</strong>的组三/组六/豹子(组选号码)
              </p>
            </div>
            <div className="flex items-center gap-1.5">
              {/* 一键采纳 AI 单码杀号推荐 */}
              <button
                onClick={() => {
                  // 跟 aiKills 取并集
                  const next = new Set(killXingtaiByCode);
                  aiKills.forEach(k => next.add(k.code));
                  setKillXingtaiByCode(next);
                  toast.success(`已采纳 AI 推荐 ${aiKills.length} 个杀组选号码`);
                }}
                className="btn-ghost text-[10px] flex items-center gap-1 py-1 px-2"
              >
                <Sparkles size={10} /> 一键 AI
              </button>
              <button
                onClick={() => {
                  setKillXingtaiByCode(new Set());
                  toast.success('已清空杀组选号码');
                }}
                className="btn-ghost text-[10px] flex items-center gap-1 py-1 px-2"
              >
                <X size={10} /> 清空
              </button>
            </div>
          </div>

          {/* 0-9 数字按钮 */}
          <div className="flex gap-1.5 flex-wrap">
            {Array.from({ length: 10 }, (_, i) => {
              const isKilled = killXingtaiByCode.has(i);
              // 从 result.killXingtai 找这个数字的规则(通过 prev/axis/etc)
              // 简单点:从 aiKills 找有没有这个数
              const aiRec = aiKills.find(k => k.code === i);
              return (
                <button
                  key={i}
                  onClick={() => {
                    setKillXingtaiByCode(prev => {
                      const next = new Set(prev);
                      if (next.has(i)) next.delete(i);
                      else next.add(i);
                      return next;
                    });
                  }}
                  className={clsx(
                    'relative w-11 h-11 rounded-lg text-base font-mono font-bold transition border',
                    isKilled
                      ? 'bg-accent-danger/25 border-accent-danger text-accent-danger line-through'
                      : aiRec
                        ? 'bg-brand-purple/10 border-brand-purple/40 text-brand-purple hover:border-accent-danger/40'
                        : 'bg-white/5 border-white/10 text-ink-1 hover:border-accent-danger/40'
                  )}
                  title={aiRec
                    ? `AI 推荐杀号: ${aiRec.source} (共识 ${aiRec.formulaCount}, 命中率 ${aiRec.hitRate}%)`
                    : isKilled
                      ? `已杀 ${i}: 排除所有含 ${i} 的组三/组六/豹子`
                      : `点选杀 ${i}:排除所有含 ${i} 的号码`
                  }
                >
                  {i}
                  {aiRec && !isKilled && (
                    <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-brand-purple text-white text-[8px] flex items-center justify-center">
                      AI
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* 统计 + 风险提示 */}
          <div className="mt-2 space-y-1">
            <div className="text-[10px] text-ink-3 flex items-center gap-3 flex-wrap">
              <span>已杀号码: <span className="text-accent-danger font-mono">{[...killXingtaiByCode].sort((a,b)=>a-b).join(', ') || '无'}</span></span>
              <span>剩余候选: <span className="text-brand-cyan font-mono">
                {Math.pow(10 - killXingtaiByCode.size, 3)}
              </span> 注</span>
            </div>
            {killXingtaiByCode.size >= 4 && (
              <div className="text-[10px] text-accent-warn flex items-start gap-1">
                <AlertCircle size={10} className="flex-shrink-0 mt-0.5" />
                <span>
                  ⚠ 杀 {killXingtaiByCode.size} 个号风险较高(候选骤减到 {Math.pow(10 - killXingtaiByCode.size, 3)} 注),容易选不到目标注数。
                </span>
              </div>
            )}
            {killXingtaiByCode.size > 0 && (
              <div className="text-[10px] text-ink-3">
                💡 杀数字 N:排除 {1000 - Math.pow(10 - killXingtaiByCode.size, 3)} 注(约 {((1 - Math.pow(0.1 * (10 - killXingtaiByCode.size), 3)) * 100).toFixed(0)}% 候选)
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card">
          <div className="text-sm font-semibold mb-3">选号配置</div>
          <div className="space-y-3">
            <div>
              <div className="text-xs text-ink-3 mb-1.5">形态</div>
              <div className="flex gap-1.5 flex-wrap">
                {[['zu6', '组六'], ['zu3', '组三'], ['mixed', '混合']].map(([v, l]) => (
                  <button
                    key={v}
                    onClick={() => setType(v)}
                    className={clsx('btn-ghost text-xs', type === v && 'bg-brand-purple/20 border-brand-purple/40 text-brand-purple')}
                  >{l}</button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs text-ink-3 mb-1.5">注数</div>
              <div className="flex gap-1.5">
                {[1, 5, 10, 20, 50, 100].map(n => (
                  <button
                    key={n}
                    onClick={() => setCount(n)}
                    className={clsx('btn-ghost text-xs flex-1', count === n && 'bg-brand-cyan/20 border-brand-cyan/40 text-brand-cyan')}
                  >{n}</button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs text-ink-3 mb-1.5">奇偶</div>
              <div className="flex gap-1.5 flex-wrap">
                {[['mixed', '混合'], ['2odd1even', '2奇1偶'], ['2even1odd', '2偶1奇'], ['allodd', '全奇'], ['alleven', '全偶']].map(([v, l]) => (
                  <button
                    key={v}
                    onClick={() => setOddEven(v)}
                    className={clsx('btn-ghost text-xs', oddEven === v && 'bg-brand-purple/20 border-brand-purple/40 text-brand-purple')}
                  >{l}</button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs text-ink-3 mb-1.5">大小</div>
              <div className="flex gap-1.5 flex-wrap">
                {[['mixed', '混合'], ['2big1small', '2大1小'], ['2small1big', '2小1大'], ['allbig', '全大'], ['allsmall', '全小']].map(([v, l]) => (
                  <button
                    key={v}
                    onClick={() => setBigSmall(v)}
                    className={clsx('btn-ghost text-xs', bigSmall === v && 'bg-brand-purple/20 border-brand-purple/40 text-brand-purple')}
                  >{l}</button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="text-sm font-semibold mb-3">权重配置</div>
          <div className="space-y-2">
            {[
              ['胆码', 'dan'],
              ['热号', 'hot'],
              ['冷号', 'cold'],
              ['对码', 'pair'],
              ['杀号', 'kill'],
            ].map(([label, key]) => (
              <div key={key}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-ink-2">{label}</span>
                  <span className="font-mono text-brand-cyan">{weights[key].toFixed(1)}</span>
                </div>
                <input
                  type="range"
                  min="0" max="3" step="0.1"
                  value={weights[key]}
                  onChange={e => setWeights(w => ({ ...w, [key]: +e.target.value }))}
                  className="w-full accent-brand-purple"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="card flex flex-col justify-between">
          <div>
            <div className="text-sm font-semibold mb-3">执行</div>
            <div className="space-y-2 text-xs text-ink-2 mb-4">
              <div>本期主胆: <span className="text-brand-gold font-mono">{danPool.mainDan}</span></div>
              <div>热号: {heatMap.hot30.join(', ')}</div>
              <div>冷号: {heatMap.cold.join(', ')}</div>
              {/* 已杀单码:合并"杀单码"和"杀组选号码"——两个都是排除含此数字的号码 */}
              {(() => {
                const allKilledCodes = new Set([...localKills, ...killXingtaiByCode]);
                return (
                  <div className="flex items-start gap-1">
                    <span>已杀单码:</span>
                    <span className="text-accent-danger font-mono font-bold text-base">
                      {allKilledCodes.size} 个
                    </span>
                    <span className="text-ink-3">
                      ({[...allKilledCodes].sort((a,b)=>a-b).join(' ') || '无'})
                    </span>
                  </div>
                );
              })()}
              <div className="flex items-center gap-1">
                <span>已杀两码:</span>
                <span className="text-accent-danger font-mono font-bold text-base">
                  {localKillPairs.size} 个
                </span>
                <span className="text-ink-3">
                  {localKillPairs.size > 0 ? `(${Array.from(localKillPairs).slice(0, 5).map(p => p.sort ? p.sort().join('') : p).join(' ')}${localKillPairs.size > 5 ? ' ...' : ''})` : '(无)'}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <span>已杀组选形态:</span>
                <span className="text-accent-danger font-mono font-bold">
                  {[
                    killXingtai.group6 && '组六',
                    killXingtai.group3 && '组三',
                    killXingtai.baozi && '豹子',
                  ].filter(Boolean).join('+') || '无'}
                </span>
              </div>
              {/* 单码细分(杀单码 vs 杀组选号码) */}
              {(localKills.size > 0 || killXingtaiByCode.size > 0) && (
                <div className="text-[10px] text-ink-3 pl-1">
                  {localKills.size > 0 && (
                    <div>· 单码: {[...localKills].sort((a,b)=>a-b).join(' ')}</div>
                  )}
                  {killXingtaiByCode.size > 0 && (
                    <div>· 组选号: {[...killXingtaiByCode].sort((a,b)=>a-b).join(' ')}</div>
                  )}
                </div>
              )}
            </div>
          </div>
          <button
            onClick={() => { setSeed(Date.now()); run(); }}
            className="btn-primary w-full flex items-center justify-center gap-2"
          >
            <Sparkles size={16} /> 重新生成
          </button>
        </div>
      </div>

      {picks.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold">生成结果 ({picks.length} 注)</div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={handleSaveSelection}
                disabled={savingSelection || !isLoggedIn}
                title={isLoggedIn ? '保存到我的选号,开奖后命中自动通知' : '请先登录'}
                className="btn-ghost text-xs flex items-center gap-1.5 disabled:opacity-50"
              >
                <Bell size={12} className="text-brand-cyan" />
                {savingSelection ? '保存中...' : '智能提醒'}
              </button>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(picks.map(p => p.code).join(' '));
                  toast.success('已复制');
                }}
                className="btn-ghost text-xs flex items-center gap-1.5"
              >
                <Copy size={12} /> 复制全部
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {picks.map((p, i) => (
              <div key={p.code} className="p-2 rounded-lg bg-white/5 border border-white/10">
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-ink-3">#{String(i+1).padStart(2,'0')}</span>
                  <div className="font-mono font-bold text-base text-brand-gold">{p.code}</div>
                </div>
                <div className="text-[10px] text-ink-3 mt-1">
                  {p.xingtai} · 跨{p.span} · 和{p.sum}
                </div>
                <div className="text-[10px] text-ink-2 mt-0.5">{p.tags}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
